import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { Modal } from '../components/UI/Modal';
import { getImageUrl } from '../config/imageUrl';
import { dishService } from '../services/dish.service';
import { Dish } from '../types/dish.types';
import { PortionPricesTable } from '../components/PortionPricesTable';

export const MenuPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { success, error } = useNotification();

  const [dishes, setDishes] = useState<Dish[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dishName, setDishName] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Состояние для раскрытых коллапсов
  const [expandedDishes, setExpandedDishes] = useState<Set<number>>(new Set());

  // Загрузка блюд
  useEffect(() => {
    const fetchDishes = async () => {
      try {
        const data = await dishService.getAllDishes();
        setDishes(data);
      } catch (err) {
        error(t('menu.error'), t('menu.errorLoadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchDishes();
  }, [error, t]);

  // Обработка изменения изображения
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Создание блюда
  const handleCreateDish = async () => {
    if (!dishName.trim()) {
      error(t('menu.errorValidation'), t('menu.errorFillAllFields'));
      return;
    }

    setIsCreating(true);
    try {
      const newDish = await dishService.createDish({
        name: dishName,
        image: imageFile || undefined,
      });

      success(t('menu.success'), t('menu.successCreated'));

      // Редирект на страницу редактирования
      navigate(`/menu/dish/${newDish.id}`);
    } catch (err) {
      error(t('menu.error'), t('menu.errorCreateFailed'));
      setIsCreating(false);
    }
  };

  // Открытие модального окна
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setDishName('');
    setImageFile(null);
    setImagePreview(null);
  };

  // Закрытие модального окна
  const handleCloseModal = () => {
    if (!isCreating) {
      setIsModalOpen(false);
      setDishName('');
      setImageFile(null);
      setImagePreview(null);
    }
  };

  // Удаление блюда
  const handleDeleteDish = async (dishId: number) => {
    if (!window.confirm(t('menu.confirmDelete'))) {
      return;
    }

    try {
      await dishService.deleteDish(dishId);
      setDishes((prev) => prev.filter((d) => d.id !== dishId));
      success(t('menu.success'), t('menu.successDeleted'));
    } catch (err) {
      error(t('menu.error'), t('menu.errorDeleteFailed'));
    }
  };

  // Переключение раскрытия коллапса
  const toggleExpanded = (dishId: number) => {
    setExpandedDishes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dishId)) {
        newSet.delete(dishId);
      } else {
        newSet.add(dishId);
      }
      return newSet;
    });
  };

  return (
    <div className="container-app py-8">
      {/* Заголовок и описание */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary mb-2">{t('menu.title')}</h1>
        <p className="text-text-secondary">{t('menu.subtitle')}</p>
      </div>

      {/* Кнопка добавления */}
      <div className="mb-6 flex justify-end">
        <button onClick={handleOpenModal} className="btn btn-primary">
          {t('menu.addNew')}
        </button>
      </div>

      {/* Карточки блюд */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-text-secondary">{t('loading')}</p>
        </div>
      ) : dishes.length === 0 ? (
        <div className="card text-center py-12 text-text-tertiary">
          {t('menu.noDishes')}
        </div>
      ) : (
        <div className="space-y-4">
          {dishes.map((dish) => {
            const isExpanded = expandedDishes.has(dish.id);

            return (
              <div
                key={dish.id}
                className="bg-white rounded-xl border-2 border-background-200 hover:border-primary-200 transition-all duration-200 overflow-hidden"
              >
                {/* Основная информация карточки */}
                <div className="p-5">
                  <div className="flex gap-4">
                    {/* Изображение */}
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 sm:w-28 sm:h-28 bg-background-100 rounded-lg overflow-hidden flex items-center justify-center">
                        {getImageUrl(dish.image) ? (
                          <img
                            src={getImageUrl(dish.image)!}
                            alt={dish.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <svg className="w-10 h-10 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Информация и кнопки */}
                    <div className="flex-1 min-w-0">
                      {/* Название */}
                      <h3 className="text-lg font-bold text-text-primary mb-3">
                        {dish.name}
                      </h3>

                      {/* Граммовки */}
                      <div className="mb-4">
                        {dish.portions.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {dish.portions.map((portion) => (
                              <span
                                key={portion.id}
                                className="inline-flex items-center text-xs px-2.5 py-1 bg-primary-50 text-primary-700 rounded-md font-medium"
                              >
                                {portion.weight}г
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-text-tertiary text-sm">{t('menu.noPortions')}</span>
                        )}
                      </div>

                      {/* Кнопки действий */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => toggleExpanded(dish.id)}
                          className={`btn btn-sm flex items-center ${isExpanded ? 'btn-primary' : 'bg-primary-50 text-primary-700 hover:bg-primary-100'}`}
                          disabled={dish.portions.length === 0}
                          title={dish.portions.length === 0 ? t('menu.noPricesHint') : undefined}
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{t('menu.actionPrices')}</span>
                          <svg
                            className={`w-4 h-4 ml-1.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigate(`/menu/dish/${dish.id}`)}
                          className="btn btn-secondary btn-sm flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>{t('menu.actionEdit')}</span>
                        </button>
                        <button
                          onClick={() => handleDeleteDish(dish.id)}
                          className="btn btn-error btn-sm flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>{t('menu.actionDelete')}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Коллапс с ценами */}
                {isExpanded && dish.portions.length > 0 && (
                  <div className="border-t border-background-200 bg-background-50 p-5">
                    <PortionPricesTable
                      portions={dish.portions}
                      onPriceUpdate={() => {
                        // Обновить данные блюда
                        dishService.getDishById(dish.id).then((updatedDish) => {
                          setDishes((prev) => prev.map((d) => (d.id === updatedDish.id ? updatedDish : d)));
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Модальное окно создания блюда */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={t('menu.modalTitle')}
        size="md"
      >
        <div className="space-y-4">
          {/* Название блюда */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('menu.fieldName')}
            </label>
            <input
              type="text"
              value={dishName}
              onChange={(e) => setDishName(e.target.value)}
              className="input-field"
              placeholder={t('menu.placeholderName')}
              disabled={isCreating}
            />
          </div>

          {/* Загрузка изображения */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('menu.fieldImage')}
            </label>
            {imagePreview && (
              <div className="mb-3 w-32 h-32 bg-background-200 rounded-lg overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isCreating}
              className="block w-full text-sm text-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="mt-2 text-xs text-text-tertiary">
              {t('menu.imageHint')}
            </p>
          </div>

          {/* Кнопки */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={handleCloseModal}
              className="btn btn-secondary"
              disabled={isCreating}
            >
              {t('menu.cancel')}
            </button>
            <button
              onClick={handleCreateDish}
              className="btn btn-primary"
              disabled={isCreating}
            >
              {isCreating ? t('menu.creating') : t('menu.create')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
